import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  CheckCircleIcon,
  WarningIcon,
  ViewIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FaUserCircle, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { motion } from "framer-motion";

// Custom components
import { deleteModalState } from "atoms/deleteModalState";
import DeleteModal from "components/modals/DeleteModal";
import CreateUserProfileDrawer from "./CreateUserProfileDrawer";
import { createUserProfileDrawerState } from "atoms/createUserProfileDrawerState";
import { MdEvStation, MdPeople } from "react-icons/md";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaUserCircle} boxSize={16} color="brand.500" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Groups Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no groups available at the moment. New groups will
      appear here once created.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status) {
    case true:
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
      break;
    case false:
      color = "red";
      icon = WarningIcon;
      label = "Inactive";
      break;
    default:
      color = "gray";
      icon = InfoIcon;
      label = "Unknown";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const ProfileAvatar = ({ name }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <Avatar
      name={name}
      size="sm"
      bg="brand.500"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
    />
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

export default function UserProfilesTable(props) {
  const { columnsData, data: propData } = props;
  const [showDeleteModal, setShowDeleteModal] =
    useRecoilState(deleteModalState);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [userProfiles, setUserProfiles] = useState(propData);
  const createDrawerState = useRecoilValue(createUserProfileDrawerState);
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => userProfiles, [userProfiles]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const name = row.original.name || "";
          const status = row.original.status || "";
          const users = row.original.users?.toString() || "";

          return (
            name.toLowerCase().includes(lowercasedQuery) ||
            status.toLowerCase().includes(lowercasedQuery) ||
            users.includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#23A979";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const handleDeleteClick = (profile) => {
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setSelectedProfile(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    // Remove the deleted profile from the list
    if (selectedProfile) {
      setUserProfiles((prev) =>
        prev.filter((profile) => profile.id !== selectedProfile.id)
      );
    }
    setShowDeleteModal(false);
    setSelectedProfile(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  const updateHandler = () => {
    // This will be called after creating a new profile
    // For now, we'll just refresh the dummy data
    // In production, this would refetch from the API
    console.log("Group created, refreshing list...");
    props.onRefresh();
  };

  // Update userProfiles when propData changes
  useEffect(() => {
    if (propData && propData.length > 0) {
      setUserProfiles(propData);
    }
  }, [propData]);

  return userProfiles.length > 0 ? (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Header with search */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search User Groups
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, users, status..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                User Groups
              </Text>
              <Badge ml={2} colorScheme="brand" borderRadius="full" px={2}>
                {userProfiles.length} total
              </Badge>
            </Flex>
          </Flex>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Name") {
                        data = (
                          <Flex align="center" gap={3}>
                            <ProfileAvatar
                              name={cell.row.original.Name || "N/A"}
                            />
                            <Box>
                              <Text color={textColor} fontWeight="semibold">
                                {cell.row.original.Name || "Unnamed Group"}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Contract ID: {cell.row.original.ContractId}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      } else if (cell.column.Header === "No Of Users") {
                        const usersCount = cell.row.original?.UserCount || 0;
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              bg="rgba(35, 169, 121, 0.1)"
                              color={themeColor}
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="rgba(35, 169, 121, 0.3)"
                              px={3}
                              py={1}
                              fontWeight="bold"
                              fontSize="sm"
                            >
                              {usersCount}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "No Of Chargers") {
                        const chargerCount =
                          cell.row.original?.ChargerCount || 0;
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              bg="rgba(5, 13, 124, 0.1)"
                              color="rgba(5, 20, 107, 1)"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="rgba(2, 17, 102, 0.3)"
                              px={3}
                              py={1}
                              fontWeight="bold"
                              fontSize="sm"
                            >
                              {chargerCount}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Status") {
                        data = (
                          <Flex align="center" justify="center">
                            <StatusBadge status={cell.row.original?.IsActive} />
                          </Flex>
                        );
                      } else if (cell.column.Header === "Actions") {
                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View & Edit Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/userprofiles/view/${cell.row.original.Id}`
                                  );
                                }}
                              />
                            </Tooltip>

                            <Tooltip
                              label="Delete Group"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(cell.row.original);
                                }}
                              />
                            </Tooltip>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {userProfiles.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        data={selectedProfile}
        title="userprofiles"
      />

      {createDrawerState?.isOpen && (
        <CreateUserProfileDrawer update={updateHandler} />
      )}
    </MotionBox>
  ) : (
    <GlassMorphismCard w="100%" py={8}>
      <EmptyStateIllustration />
      <CreateUserProfileDrawer update={updateHandler} />
    </GlassMorphismCard>
  );
}
